import React, { useCallback, useMemo, useState } from "react";
import SyncLatestModal from "./SyncLatestModal";
import ViewHistoryModal from "./ViewHistoryModal";
import { columnValuesResync, SharedColumnsProps } from "./resynchronization.interface";
import DisplayValueComponent from "./DisplayValueComponent";
import DiscrepancyGrid from "./DiscrepancyGrid";
import MondayValueCell from "./DiscrepancyGrid/MondayValueCell";
import { buildColumnUpdates, toBoardHeaders, toDiscrepancyRows } from "./sharedColumnsAdapter";

/**
 * The Shared Items body. Two responsibilities only: owning the two modals' state, and
 * adapting the item-view props into grid rows via sharedColumnsAdapter. Everything about
 * layout, filtering, skeletons and virtualization belongs to DiscrepancyGrid.
 */
const SharedColumns = ({
  syncId,
  dbItems,
  discrepancyItems,
  sharedColumns,
  filter,
  applyOptimisticUpdate,
  onResynced,
  isLoading = false,
}: SharedColumnsProps) => {
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentColumnType, setCurrentColumnType] = useState<string | null>(null);
  const [currentColumnTitle, setCurrentColumnTitle] = useState<string | null>(null);
  const [currentSyncId, setCurrentSyncId] = useState<string | null>(null);
  const [itemColumnValues, setItemColumnValues] = useState<columnValuesResync[] | null>(null);
  const [columnsHistory, setColumnsHistory] = useState<any>(null);

  // useCallback-stable so it can be a genuine dependency of the `rows` memo
  // below, rather than an omission the memo quietly relies on.
  const handleSyncLatestClick = useCallback(
    (columnType: string, columnTitle: string) => {
      setCurrentSyncId(syncId);
      setCurrentColumnTitle(columnTitle);
      setCurrentColumnType(columnType);
      // Derived on demand from props the parent already holds, rather than accumulated in
      // state by a child's render-time effect. SyncLatestModal builds its resyncPayload from
      // this, so an empty array here is what leaves the modal stuck on "Loading...".
      setItemColumnValues(buildColumnUpdates(dbItems, discrepancyItems, columnType, columnTitle));
      setIsModalOpen(true);
    },
    [syncId, dbItems, discrepancyItems],
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentColumnType(null);
    setCurrentColumnTitle(null);
    setCurrentSyncId(null);
  };

  // Same reasoning as handleSyncLatestClick: useCallback-stable so it is safe
  // to list as a real dependency rather than silently excluded.
  const handleViewHistoryClick = useCallback(
    (columnType: string, columnTitle: string) => {
      setColumnsHistory(discrepancyItems);
      setCurrentColumnType(columnType);
      setCurrentColumnTitle(columnTitle);
      setCurrentSyncId(syncId);
      setIsHistoryModalOpen(true);
    },
    [discrepancyItems, syncId],
  );

  const handleCloseHistoryModal = () => {
    setColumnsHistory(null);
    setIsHistoryModalOpen(false);
  };

  // `filter` is a loose string here but a strict union on the grid. The predicate this
  // replaces was `(!filter || filter === "discrepancies") ? discrepancy : true`, so an absent
  // filter has always meant "discrepancies" — normalise it the same way round.
  const gridFilter = filter === "discrepancies" || !filter ? "discrepancies" : "all";

  const boards = useMemo(
    () => toBoardHeaders(dbItems, discrepancyItems, isLoading),
    [dbItems, discrepancyItems, isLoading],
  );

  // The shared columns are known before their values are (they ride on the cached flag
  // row), but a row built without values marks every board "Column does not exist on
  // this board". Withheld while loading for that reason — but the identity must still
  // be stable across renders, or the grid's own useMemo never holds.
  const rows = useMemo(
    () =>
      isLoading
        ? []
        : toDiscrepancyRows({
            syncId,
            dbItems,
            discrepancyItems,
            sharedColumns: sharedColumns ?? [],
            onSyncLatest: handleSyncLatestClick,
            onViewHistory: handleViewHistoryClick,
          }),
    [isLoading, syncId, dbItems, discrepancyItems, sharedColumns, handleSyncLatestClick, handleViewHistoryClick],
  );

  return (
    <>
      <DiscrepancyGrid
        boards={boards}
        rows={rows}
        filter={gridFilter}
        isLoading={isLoading}
        // The old markup rendered nothing at all when every column was filtered out; the grid
        // always renders an empty state, so give it copy that names what is actually missing.
        emptyMessage={
          gridFilter === "discrepancies"
            ? "No discrepancies in this item."
            : "No shared columns to display."
        }
        renderValue={(value, row) => {
          // Threaded through `meta` by toDiscrepancyRows rather than parsed back out of
          // `row.id`, which a column title containing a hyphen would break.
          const columnType = (row.meta?.columnType as string) ?? null;

          // board_relation stays with DisplayValueComponent, which resolves linked item NAMES
          // asynchronously through the Monday service (getItemNames). MondayValueCell is pure
          // and synchronous by design, so routing this type through it would render raw
          // linked-pulse ids — a regression on the one column type DisplayValueComponent
          // actually special-cases.
          if (columnType === "board_relation") {
            return (
              <DisplayValueComponent
                columnExists={value?.exists ?? false}
                columnValue={value?.value ?? null}
                columnText={value?.text ?? null}
                columnType={columnType}
                linkedItemIds={value?.linkedItemIds ?? null}
              />
            );
          }

          // Everything else gets the same Monday-native shapes as the Subitems tab, so the two
          // tabs stop disagreeing about what a value looks like. Status grounds are the
          // customer's own label colours: simb-utils-ms's item-query-batch.ts requests them via
          // the StatusValue fragment, findDiscrepancies passes column_values through verbatim,
          // and sharedColumnsAdapter's toDisplayValue lifts label_style.color onto labelColor.
          // Only a status Monday reports no colour for falls back to a theme token — that
          // fallback used to catch EVERY status, because the fragment was missing from the
          // query and this comment described the subitem engine's copy of it.
          return (
            <MondayValueCell
              value={value}
              columnType={columnType}
              dropdownLabels={(value as { dropdownLabels?: string[] } | null)?.dropdownLabels}
            />
          );
        }}
      />

      {/* Modals */}
      <SyncLatestModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        columnType={currentColumnType}
        columnTitle={currentColumnTitle}
        syncId={currentSyncId}
        itemColumnValues={itemColumnValues}
        applyOptimisticUpdate={applyOptimisticUpdate}
        onResynced={onResynced}
        boards={boards}
      />
      <ViewHistoryModal
        columns={columnsHistory}
        isOpen={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        columnType={currentColumnType}
        columnTitle={currentColumnTitle}
      />
    </>
  );
};

export default SharedColumns;
